import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { checkEntryCopy, MAX_DESCRIPTION, MAX_TITLE } from "@shared/changelog/entryCopy";
import {
  checkReleaseEntry,
  EMPTY_RELEASE_ENTRY,
  ReleaseEntryDraft,
} from "@shared/changelog/releaseEntry";
import type { ChangeCategory } from "@shared/changelog/types";
import {
  getVersions,
  createRelease,
  updateRelease,
  ReleaseRecord,
  ServiceVersions,
} from "./request/request";

export const RELEASE_SERVICES = [
  "sync",
  "uc",
  "ha",
  "naboo",
  "endor",
  "cli",
  "gobgpd",
  "bfdd",
  "bfd-gobgp-connector",
  "shell",
  "log-forwarder",
  "fluent-bit",
];

const CHANNEL_OPTIONS = ["stable", "beta"];

/** The closed set, in the order the entry contract lists it. */
const CHANGELOG_CATEGORIES: readonly ChangeCategory[] = [
  "feature",
  "improvement",
  "fix",
  "security",
  "deprecation",
  "breaking",
];

/**
 * Distinct from the `None` the service dropdowns use, which means "this service
 * is not part of the release" — a real choice. This one means the field has no
 * value yet, and it exists so an author who filled the entry and then decided
 * not to announce can get back to no entry at all.
 */
const NOT_SET = "Not set";

/**
 * Gate 4 as two labelled choices, with no default. Both audiences are
 * customers; what separates them is whether the sentence belongs on the open
 * internet. A default either leaks business detail on one forgotten toggle or
 * leaves the public feed permanently empty, so the author picks.
 */
const PUBLIC_CHOICES: readonly { label: string; value: boolean }[] = [
  { label: "Public — also on tryctx.com", value: true },
  { label: "In-app only — signed-in customers", value: false },
];

function publicLabelFor(value: boolean | null): string {
  return PUBLIC_CHOICES.find((choice) => choice.value === value)?.label ?? NOT_SET;
}

/**
 * The rules `entryCopy` and the entry contract enforce, said out loud.
 *
 * Whoever promotes a release is not the person who wrote the changelog
 * strategy, and the validator only speaks after they have already written
 * something wrong. These say what belongs in each field *before* that.
 *
 * Deliberately concrete rather than a paraphrase of the rule: "no file names,
 * snake_case or code" is actionable, "write for customers" is not.
 */
const FIELD_HELP: Record<string, string> = {
  title:
    `A ${MAX_TITLE}-character headline a customer would recognise — what changed, ` +
    "not how. No file names, snake_case, code, source paths, or words like " +
    '"refactor". "Endpoint" and "component" are fine in the description but ' +
    "not here: a headline has no room for implementation nouns.",
  description:
    `Up to ${MAX_DESCRIPTION} characters — one or two sentences saying what the ` +
    "customer can now do, know, or must act on. Same banned vocabulary as the " +
    "title. Never name internal systems, incidents, tickets or other customers.",
  actionRequired:
    "One sentence if the customer has to do something — upgrading reflectors is " +
    "the usual case. Leaving it empty is a claim that they need not, so leave it " +
    "empty only when that is true.",
  category:
    "Picks the badge the entry renders with. A closed set shared with the entry " +
    "files and the backend column, so it cannot be extended here — pick the " +
    "closest one rather than the most flattering.",
  visibility:
    "Both audiences are customers; what differs is the open internet. Public " +
    "also publishes to tryctx.com, where anyone can read it and search engines " +
    "index it. Choose it only if you would put the same sentence in a company " +
    "tweet. No default exists on purpose — one wrong toggle either leaks " +
    "business detail or leaves the public feed empty.",
};

/** Repeats five times in the entry block; the tooltip is the whole point of it. */
const EntryFieldLabel: React.FC<{ label: string; help: string }> = ({
  label,
  help,
}) => (
  <span className="flex items-center gap-1.5 text-sm font-medium text-semantic-text-secondary">
    {label}
    <GenericTooltip text={help} position="top" width="320px" />
  </span>
);

interface Props {
  isOpen: boolean;
  onClose: (refreshNeeded?: boolean) => void;
  release: ReleaseRecord | null; // null = create mode, object = edit mode
}

const CreateReleaseModal: React.FC<Props> = ({ isOpen, onClose, release }) => {
  const { getAccessTokenSilently } = useAuth0();
  const isEditMode = release !== null;

  // Form state
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("stable");
  const [notes, setNotes] = useState("");
  const [components, setComponents] = useState<Record<string, string>>({});
  const [entry, setEntry] = useState<ReleaseEntryDraft>(EMPTY_RELEASE_ENTRY);

  const patchEntry = useCallback(
    (patch: Partial<ReleaseEntryDraft>) =>
      setEntry((prev) => ({ ...prev, ...patch })),
    []
  );

  // Available versions per service from API
  const [availableVersions, setAvailableVersions] = useState<
    Record<string, ServiceVersions>
  >({});
  const [loadingVersions, setLoadingVersions] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch available versions
  const fetchVersions = useCallback(async () => {
    try {
      setLoadingVersions(true);
      const token = await getAccessTokenSilently();
      const res = await getVersions(token);
      if (res.data.success) {
        setAvailableVersions(res.data.data);
      }
    } catch (err) {
      console.error("[CreateReleaseModal] Error fetching versions:", err);
    } finally {
      setLoadingVersions(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isOpen) {
      fetchVersions();
    }
  }, [isOpen, fetchVersions]);

  // Pre-populate form in edit mode
  useEffect(() => {
    if (release) {
      setName(release.name);
      setChannel(release.channel);
      setNotes(release.notes || "");
      setComponents({ ...release.components });
      // `??` and not `||`: the record's changelog fields are absent until the
      // release controller returns them, and `changelogIsPublic: false` is a
      // real decision that `||` would silently turn back into "not set".
      setEntry({
        title: release.changelogTitle ?? "",
        description: release.changelogDescription ?? "",
        category: release.changelogCategory ?? null,
        isPublic: release.changelogIsPublic ?? null,
        actionRequired: release.changelogActionRequired ?? "",
      });
    } else {
      setName("");
      setChannel("stable");
      setNotes("");
      setComponents({});
      setEntry(EMPTY_RELEASE_ENTRY);
    }
    setError(null);
  }, [release, isOpen]);

  /**
   * Copy problems only, and only for text that has been written. The whole
   * rule — including the fields still empty — runs on save: nagging about a
   * missing category while the author is still typing the title reads as the
   * form arguing with them.
   */
  const titleProblems = useMemo(
    () => (entry.title.trim() ? checkEntryCopy("title", entry.title.trim()) : []),
    [entry.title]
  );
  const descriptionProblems = useMemo(
    () =>
      entry.description.trim()
        ? checkEntryCopy("description", entry.description.trim())
        : [],
    [entry.description]
  );

  // Handlers
  const handleServiceVersionChange = (service: string, version: string) => {
    setComponents((prev) => {
      const updated = { ...prev };
      if (version === "" || version === "None") {
        delete updated[service];
      } else {
        updated[service] = version;
      }
      return updated;
    });
  };

  const handleSave = async () => {
    setError(null);

    if (!name.trim()) {
      setError("Release name is required");
      return;
    }

    if (Object.keys(components).length === 0) {
      setError("Select at least one service version");
      return;
    }

    // The entry is optional as a group and mandatory as a set. Rejecting here
    // is the point of validating in the form: at promotion time the author is
    // no longer around to fix it (CTX-456).
    const entryProblems = checkReleaseEntry(entry);
    if (entryProblems.length > 0) {
      setError(`Changelog entry: ${entryProblems.join(" ")}`);
      return;
    }

    // Empty text becomes null rather than "": the columns are nullable, and an
    // empty string would read as an entry whose title happens to be blank.
    const changelogFields = {
      changelogTitle: entry.title.trim() || null,
      changelogDescription: entry.description.trim() || null,
      changelogCategory: entry.category,
      changelogIsPublic: entry.isPublic,
      changelogActionRequired: entry.actionRequired.trim() || null,
    };

    try {
      setSaving(true);
      const token = await getAccessTokenSilently();

      if (isEditMode && release) {
        await updateRelease(
          release.id,
          { name, channel, notes, components, ...changelogFields },
          token
        );
      } else {
        await createRelease(
          { name, channel, notes, components, ...changelogFields },
          token
        );
      }

      onClose(true);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      const msg =
        axiosErr?.response?.data?.message || "Failed to save release";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  // Build version options for each service dropdown
  const getVersionOptions = (service: string): string[] => {
    const svc = availableVersions[service];
    if (!svc?.versions) return [];
    return svc.versions.map((v) => v.version);
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={() => onClose(false)}
      title={isEditMode ? `Edit Release: ${release?.name}` : "Create Release"}
      size="lg"
    >
      <div className="flex flex-col h-full">
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-4 max-h-[70vh]">
          <div className="flex flex-col gap-4">
            {error && (
              <GenericAlert
                isOpen={!!error}
                onClose={() => setError(null)}
                onConfirm={() => setError(null)}
                title="Validation Error"
                description={error}
                confirmLabel="OK"
                variant="error"
                hideCancel={true}
              />
            )}

            {/* Name */}
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-semantic-text-secondary">
                Release Name
              </span>
              <GenericInput
                value={name}
                onChange={(val) => setName(val)}
                placeholder="e.g. v2.1.0"
              />
            </div>

            {/* Channel */}
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-semantic-text-secondary">
                Channel
              </span>
              <GenericDropdown
                items={CHANNEL_OPTIONS}
                value={channel}
                onSelect={(val) => setChannel(val)}
                defaultLabel="Select channel"
              />
            </div>

            {/* Notes */}
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-semantic-text-secondary">
                Notes (optional)
              </span>
              <textarea
                className="w-full rounded border border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-primary p-2 text-sm min-h-[60px] resize-y"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Release notes..."
              />
            </div>

            {/* Changelog entry (CTX-456) — optional as a group, mandatory as a
                set. `notes` above is the technical note an admin reads while
                updating reflectors; this is the sentence a customer reads. */}
            <div className="flex flex-col gap-3 rounded border border-semantic-border-secondary p-3">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-semantic-text-primary">
                  Changelog entry (optional)
                </span>
                <span className="text-xs text-semantic-text-secondary">
                  What customers will read when this release is promoted. Leave
                  it empty for an internal patch or a rebuild — but if you fill
                  in one field, fill in all four.
                </span>
              </div>

              <div className="flex flex-col gap-1">
                <EntryFieldLabel label="Title" help={FIELD_HELP.title} />
                <GenericInput
                  value={entry.title}
                  onChange={(val) => patchEntry({ title: val })}
                  placeholder="e.g. Faster failover for high availability pairs"
                  hasError={titleProblems.length > 0}
                  helperText={
                    titleProblems[0] ??
                    `${entry.title.trim().length}/${MAX_TITLE} characters`
                  }
                  fullWidth
                />
              </div>

              <div className="flex flex-col gap-1">
                <EntryFieldLabel
                  label="Description"
                  help={FIELD_HELP.description}
                />
                <textarea
                  className="w-full rounded border border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-primary p-2 text-sm min-h-[60px] resize-y"
                  value={entry.description}
                  onChange={(e) => patchEntry({ description: e.target.value })}
                  placeholder="One or two sentences, written to a customer."
                />
                {/* `text-red-500` and not a semantic token: it is what
                    GenericInput uses for an error, and the two helper lines sit
                    next to each other. */}
                <span
                  className={`text-xs ${
                    descriptionProblems.length > 0
                      ? "text-red-500"
                      : "text-semantic-text-secondary"
                  }`}
                >
                  {descriptionProblems[0] ??
                    `${entry.description.trim().length}/${MAX_DESCRIPTION} characters`}
                </span>
              </div>

              {/* Optional within the entry, matching `action_required` in the
                  entry files. Prompted rather than hidden because empty is a
                  claim: it tells the customer there is nothing to do, and for a
                  reflector release that is often untrue. */}
              <div className="flex flex-col gap-1">
                <EntryFieldLabel
                  label="Action required"
                  help={FIELD_HELP.actionRequired}
                />
                <textarea
                  className="w-full rounded border border-semantic-border-secondary bg-semantic-bg-primary text-semantic-text-primary p-2 text-sm min-h-[44px] resize-y"
                  value={entry.actionRequired}
                  onChange={(e) => patchEntry({ actionRequired: e.target.value })}
                  placeholder="e.g. Upgrade your reflectors to 2.0.18 to pick up the change."
                />
                <span className="text-xs text-semantic-text-secondary">
                  Leave empty only if the customer truly has nothing to do —
                  most reflector releases ask them to upgrade.
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex flex-col gap-1">
                  <EntryFieldLabel
                    label="Category"
                    help={FIELD_HELP.category}
                  />
                  <GenericDropdown
                    items={[NOT_SET, ...CHANGELOG_CATEGORIES]}
                    value={entry.category ?? NOT_SET}
                    onSelect={(val) =>
                      patchEntry({
                        category:
                          val === NOT_SET ? null : (val as ChangeCategory),
                      })
                    }
                    defaultLabel={NOT_SET}
                    maxWidthClass="max-w-full"
                    fixedPosition
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <EntryFieldLabel
                    label="Visibility"
                    help={FIELD_HELP.visibility}
                  />
                  <GenericDropdown
                    items={[NOT_SET, ...PUBLIC_CHOICES.map((c) => c.label)]}
                    value={publicLabelFor(entry.isPublic)}
                    onSelect={(val) =>
                      patchEntry({
                        isPublic:
                          PUBLIC_CHOICES.find((c) => c.label === val)?.value ??
                          null,
                      })
                    }
                    defaultLabel={NOT_SET}
                    maxWidthClass="max-w-full"
                    fixedPosition
                  />
                </div>
              </div>

              <span className="text-xs text-semantic-text-secondary">
                Both audiences are customers. Choose public only if you would
                put the same sentence in a company tweet — anything naming
                internal systems, incidents or customers stays in-app.
              </span>
            </div>

            {/* Service version selectors */}
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-semantic-text-secondary">
                Service Versions
              </span>
              {loadingVersions ? (
                <p className="text-sm text-semantic-text-secondary">
                  Loading versions...
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {RELEASE_SERVICES.map((svc) => {
                    const options = getVersionOptions(svc);
                    const currentValue = components[svc] || "";
                    return (
                      <div key={svc} className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-semantic-text-secondary uppercase">
                          {svc}
                        </span>
                        <GenericDropdown
                          items={["None", ...options]}
                          value={currentValue || "None"}
                          onSelect={(val) =>
                            handleServiceVersionChange(svc, val)
                          }
                          defaultLabel="Select version"
                          maxWidthClass="max-w-full"
                          fixedPosition
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="text-sm text-semantic-text-secondary">
              {Object.keys(components).length} of {RELEASE_SERVICES.length} services
              selected
              {(() => {
                const count = Object.keys(components).length;
                if (count >= RELEASE_SERVICES.length) return " (full release)";
                if (count > 0) return " (partial release)";
                return "";
              })()}
            </div>
          </div>
        </div>

        {/* Fixed button area at bottom */}
        <div className="p-4 bg-semantic-bg-primary">
          <div className="flex justify-end gap-2">
            <GenericButton
              label="Cancel"
              variant="secondary"
              onClick={() => onClose(false)}
            />
            <GenericButton
              label={saving ? "Saving..." : (isEditMode ? "Update" : "Create")}
              variant="brand"
              onClick={handleSave}
              disabled={saving}
            />
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

export default CreateReleaseModal;
